// 1. React
import { memo, useEffect, useMemo, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. UI
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";

// 4. API / Types
import { FleetVehicleOfferSaleRow } from "services/api/fleet/vehicles/offers/types";
import { initialDataWithPagination } from "services/api/consts";
import { getFleetVehicleOfferSalesAPI } from "services/api/fleet/vehicles/offers/api";
import { DataWithPagination } from "services/api";

// 5. Charts
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";

import type { Formatter } from "recharts/types/component/DefaultTooltipContent";

import { OfferSaleStatsProps } from "./types";

// 6. Colors
const BAR_COLORS = [
    "#7C3AED",
    "#FACC15",
];

const OfferSaleStats: React.FC<OfferSaleStatsProps> = ({
    vehicleId,
}) => {
    const { t } = useTranslate();

    const [searchResults, setSearchResults] =
        useState<DataWithPagination<FleetVehicleOfferSaleRow>>(
            initialDataWithPagination
        );

    const [periodDays, setPeriodDays] = useState<7 | 30 | 90>(30);

    // FETCH
    useEffect(() => {
        getFleetVehicleOfferSalesAPI(
            searchResults.pagination
            // vehicleId,
        ).then(setSearchResults);
    }, [vehicleId]);

    // PERIOD FILTER
    const filteredByPeriod = useMemo(() => {
        const now = new Date();
        const from = new Date();
        from.setDate(now.getDate() - periodDays);

        return searchResults.data.filter((r) => {
            if (!r.offerDate) return false;
            const d = new Date(r.offerDate);
            return d >= from && d <= now;
        });
    }, [searchResults.data, periodDays]);

    // KPI – DINAMIČKI PO PERIODU
    const kpiStats = useMemo(() => {
        const now = new Date();

        const currentFrom = new Date();
        currentFrom.setDate(now.getDate() - periodDays);

        const previousFrom = new Date();
        previousFrom.setDate(now.getDate() - periodDays * 2);

        const currentPeriod = searchResults.data.filter((r) => {
            if (!r.offerDate) return false;
            const d = new Date(r.offerDate);
            return d >= currentFrom && d <= now;
        });

        const previousPeriod = searchResults.data.filter((r) => {
            if (!r.offerDate) return false;
            const d = new Date(r.offerDate);
            return d >= previousFrom && d < currentFrom;
        });

        const avgValue =
            currentPeriod.reduce((sum, r) => sum + (r.total ?? 0), 0) /
            (currentPeriod.length || 1);

        const changePercent =
            previousPeriod.length === 0
                ? 100
                : ((currentPeriod.length - previousPeriod.length) /
                    previousPeriod.length) *
                100;

        return {
            count: currentPeriod.length,
            avgValue,
            changePercent,
        };
    }, [searchResults.data, periodDays]);

    // CHART DATA – SVAKA PONUDA
    const offerChartData = useMemo(() => {
        return filteredByPeriod.map((row, index) => {
            const date = new Date(row.offerDate!);

            const clientName =
                row.contactClient?.companyName ||
                `${row.contactClient?.firstName ?? ""} ${row.contactClient?.lastName ?? ""
                    }`.trim() ||
                "Nepoznat klijent";

            return {
                xKey: row.id,
                label: date.toISOString().split("T")[0],
                total: row.total ?? 0,
                clientName,
                raw: row,
            };
        });
    }, [filteredByPeriod]);


    // TOOLTIP
    const tooltipFormatter: Formatter<
        string | number | readonly (string | number)[],
        string
    > = (value, _name, props) => {
        const numericValue =
            typeof value === "number"
                ? value.toFixed(2)
                : Array.isArray(value)
                    ? value.join(", ")
                    : value ?? "0";

        return [
            `${numericValue} €`,
            `Klijent: ${props?.payload?.clientName ?? "Nepoznat"}`,
        ];
    };

    // CLICK
    const handleBarClick = (data: any) => {
        const client = data?.raw?.contactClient;
        alert(
            client
                ? `Ponuda za: ${client.companyName ||
                `${client.firstName ?? ""} ${client.lastName ?? ""}`
                }`
                : "Ponuda nema klijenta"
        );
    };

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;

        const data = payload[0].payload;

        return (
            <div className="bg-white border border-gray-200 rounded-md px-2.5 py-2 text-xs font-inter">
                <div className="font-semibold">{data.clientName}</div>
                <div>{data.total.toFixed(2)} €</div>
                <div className="text-gray-500">{data.label}</div>
            </div>

        );
    };

    if (!searchResults.data.length)
        return <NoResults title={t("admin.finance.offer.sale.stats.noresults")} />;

    return (
        <div className="p-4 space-y-3">
            {/* FILTER */}
            <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                    <Button
                        key={d}
                        size="small"
                        variant={periodDays === d ? "primary" : "outlined"}
                        onClick={() => setPeriodDays(d as 7 | 30 | 90)}
                    >
                        {t(`Zadnjih ${d} dana`)}
                    </Button>
                ))}
            </div>

            {/* KPI */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border rounded-sm p-3">
                    <div className="text-xs text-gray-500">
                        {t(`Ponude (${periodDays} dana)`)}
                    </div>
                    <div className="text-2xl font-semibold">{kpiStats.count}</div>
                </div>

                <div className="border rounded-sm p-3">
                    <div className="text-xs text-gray-500">
                        {t("admin.finance.offer.sale.stats.change")}
                    </div>
                    <div
                        className={`text-2xl font-semibold ${kpiStats.changePercent >= 0
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                    >
                        {kpiStats.changePercent.toFixed(1)}%
                    </div>
                </div>

                <div className="border rounded-sm p-3">
                    <div className="text-xs text-gray-500">
                        {t("admin.finance.offer.sale.stats.averagevalue")}
                    </div>
                    <div className="text-2xl font-semibold">
                        {kpiStats.avgValue.toFixed(2)} €
                    </div>
                </div>
            </div>

            {/* BAR CHART */}
            <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={offerChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="xKey"
                            tickFormatter={(_, index) => offerChartData[index]?.label}
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={90}
                            axisLine={false}
                            tickLine={false}
                            tick={{
                                fontSize: 12,
                                fontFamily: "Inter, sans-serif",
                            }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{
                                fontSize: 12,
                                fontFamily: "Inter, sans-serif",
                            }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                            dataKey="total"
                            maxBarSize={64}
                            radius={[6, 6, 0, 0]}
                            onClick={handleBarClick}
                            isAnimationActive={true}
                            animationDuration={700}
                            animationEasing="ease-out">
                            {offerChartData.map((_, i) => (
                                <Cell
                                    key={i}
                                    fill={BAR_COLORS[i % BAR_COLORS.length]}
                                    cursor="pointer"
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default memo(OfferSaleStats);